// external library
import type { MouseEventHandler, ReactNode } from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// interstate components

// action and selectors
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as rollToActionCreators from '@makemydeal/dr-dash-store/actions/rollToActionCreators';
import * as rollToSelectors from '@makemydeal/dr-dash-store/selectors/rollToSelectors';

// custom internal components
import { DiscardChangesModal } from '@makemydeal/dr-shared-components';
import { StyledSlideOut } from './RollToStyles';

// error boundary component
import { Box } from '@interstate/components/Box';
import RollToAlertBoundary from './RollToAlertBoundary';

type RollToSlideOutProps = {
    show: boolean;
    onHide: MouseEventHandler;
    header?: string;
    children: ReactNode;
};

const RollToSlideOut: React.FC<RollToSlideOutProps> = ({ show, onHide, header, children }) => {
    const dispatch = useDispatch();
    const [showDiscardWarning, setShowDiscardWarning] = useState(false);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const hasRollToErrors = useSelector(rollToSelectors.hasRollToErrors);
    const hasAdjustedValues = useSelector(rollToSelectors.hasAdjustedValues);
    const hasUnsavedChanges = useSelector(rollToSelectors.getRollToUnsavedChanges);
    const applyRollToPayload = useSelector(rollToSelectors.getApplyRollToPayload);
    const disabledApplyButton = hasRollToErrors || !hasAdjustedValues || showDiscardWarning;

    const handleCancel = (e: React.MouseEvent) => {
        if (hasUnsavedChanges) {
            setShowDiscardWarning(true);
        } else {
            onHide(e);
        }
    };

    const handleGoBack = () => {
        setShowDiscardWarning(false);
    };

    const handleDiscardChanges = () => {
        setShowDiscardWarning(false);
        onHide({} as React.MouseEvent);
    };

    const applyRollToAndClose = () => {
        dispatch(rollToActionCreators.applyRollToOptions(applyRollToPayload, offerType));
        dispatch(interactionAnalyticsActionCreators.clickRollToPaymentApplyButton());
    };

    const handleApplyChanges = () => {
        if (!disabledApplyButton) {
            applyRollToAndClose();
        }
    };

    return (
        <StyledSlideOut
            data-testid="roll-to-slideout"
            show={show}
            position="right"
            onHide={handleCancel}
            header={header}
            footer={{
                layout: 'rightAligned',
                options: [
                    {
                        action: handleCancel,
                        label: 'Cancel'
                    }
                ],
                primary: {
                    action: handleApplyChanges,
                    label: 'Apply',
                    disabled: disabledApplyButton
                }
            }}
        >
            <DiscardChangesModal
                show={showDiscardWarning}
                onDiscard={handleDiscardChanges}
                onGoBack={handleGoBack}
                hasValidationErrors={hasRollToErrors}
            />
            <RollToAlertBoundary header="Error">
                <Box
                    sx={{
                        overflowY: 'auto'
                    }}
                    className="roll-to-slideout-content"
                    data-testid="roll-to-slideout-content"
                >
                    {children}
                </Box>
            </RollToAlertBoundary>
        </StyledSlideOut>
    );
};

export default RollToSlideOut;
